import { useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ChevronDown, ChevronRight, IterationCw } from 'lucide-react';
import { useNodeStore } from '@/stores/node-store';
import type { CustomNode } from '@/types/node-types';
import {
  describeForEachRegionsCached,
  forEachContaining,
  forEachRole,
  type ForEachRegion,
  type ForEachRole,
} from '@/pages/workflow/helper/for-each-region';
import { nodeLabel } from '@/pages/workflow/helper/repeat-loop';
import {
  forEachNodeRun,
  type ForEachNodeRun,
} from '@/pages/workflow/helper/map-panel';

export interface ForEachMembership {
  region: ForEachRegion;
  role: ForEachRole;
  carrierName: string;
  /** What this node did per item on the last run, if the Map ran. */
  run: ForEachNodeRun | null;
}

/** Which For-each body (if any) a node is in, and what it did there on the last run. */
export function useForEachMembership(nodeId: string): ForEachMembership | null {
  const { nodes, edges } = useNodeStore(
    useShallow((state) => ({ nodes: state.nodes, edges: state.edges })),
  );
  return useMemo(() => {
    const regions = describeForEachRegionsCached(nodes, edges);
    const region = forEachContaining(regions, nodeId);
    if (!region) return null;
    const role = forEachRole(region, nodeId);
    if (!role) return null;
    const carrier = nodes.find(
      (node: CustomNode) => node.id === region.carrierId,
    );
    return {
      region,
      role,
      carrierName: nodeLabel(carrier),
      run: forEachNodeRun(carrier?.data?.result, nodeId),
    };
  }, [nodes, edges, nodeId]);
}

const ROLE_WORDS: Record<ForEachRole, string> = {
  entry: 'first step',
  exit: 'collected',
  only: 'first step, collected',
  step: 'inside',
};

const runWords = (run: ForEachNodeRun): string => {
  const ran = `ran on ${run.ran} of ${run.total} ${run.total === 1 ? 'item' : 'items'}`;
  return run.skipped ? `${ran}, skipped ${run.skipped}` : ran;
};

/**
 * The chip a node inside a For-each body wears on the canvas: "for each"
 * before a run, "ran on 5 of 5 items" after one. Body nodes never read as
 * skipped; a branch inside the body says how many items skipped it.
 */
export function ForEachRegionChip({ nodeId }: { nodeId: string }) {
  const membership = useForEachMembership(nodeId);
  if (!membership) return null;
  const { role, run, carrierName } = membership;
  return (
    <div
      className='mx-3 mb-1 flex items-center gap-1.5 rounded-md border border-teal-200 dark:border-teal-500/40 bg-teal-50/80 dark:bg-teal-500/15 px-2 py-1 text-[11px] text-teal-900 dark:text-teal-100'
      data-testid='for-each-region-chip'
      data-role={role}
      title={`${ROLE_WORDS[role]} of the For each "${carrierName}"`}
    >
      <IterationCw className='size-3' />
      <span className='font-medium'>{run ? runWords(run) : 'for each'}</span>
      <span className='truncate text-teal-800/80 dark:text-teal-200/80'>
        · {ROLE_WORDS[role]}
      </span>
    </div>
  );
}

/** In a body node's config panel: this node's output (or skip) for each item. */
export function ForEachRegionCard({ nodeId }: { nodeId: string }) {
  const membership = useForEachMembership(nodeId);
  const [open, setOpen] = useState<number | null>(null);
  if (!membership) return null;
  const { run, role, carrierName } = membership;
  return (
    <section
      className='mx-3 my-2 space-y-2 rounded-md border border-teal-200 dark:border-teal-500/40 bg-teal-50/60 dark:bg-teal-500/15 p-2 text-xs'
      data-testid='for-each-region-card'
    >
      <div className='flex items-center gap-1.5 font-semibold text-teal-900 dark:text-teal-100'>
        <IterationCw className='size-3.5' />
        {role === 'entry' || role === 'only'
          ? 'Each item arrives here'
          : role === 'exit'
            ? 'This output is collected for each item'
            : 'This node runs once per item'}
      </div>
      <p className='text-[11px] text-teal-900/80 dark:text-teal-100/80'>
        Part of the For each <strong>{carrierName}</strong>. Its result for
        every item is kept on the For each node, in input order.
      </p>
      {run ? (
        <div className='space-y-1'>
          <div className='text-[11px] font-medium text-teal-900 dark:text-teal-100'>
            {runWords(run)} on the last run
          </div>
          <ol className='max-h-56 space-y-1 overflow-y-auto'>
            {run.items.map(({ index, step }) => {
              const isOpen = open === index;
              return (
                <li
                  key={index}
                  className='rounded border bg-background'
                  data-testid={`for-each-node-item-${index}`}
                >
                  <button
                    type='button'
                    className='flex w-full items-center gap-1.5 px-2 py-1 text-left text-[11px]'
                    onClick={() => setOpen(isOpen ? null : index)}
                  >
                    {isOpen ? (
                      <ChevronDown className='size-3' />
                    ) : (
                      <ChevronRight className='size-3' />
                    )}
                    Item {index + 1}
                    <span className='ml-1 truncate text-muted-foreground'>
                      {step.skipSentence ?? step.preview}
                    </span>
                  </button>
                  {isOpen && step.status === 'completed' && (
                    <pre className='max-h-40 overflow-auto border-t p-1.5 text-[10px] leading-4'>
                      {JSON.stringify(step.value, null, 1)}
                    </pre>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      ) : (
        <div className='text-[11px] text-teal-900/80 dark:text-teal-100/80'>
          Not run yet.
        </div>
      )}
    </section>
  );
}
